import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "../lib/api";
import type {
  Block,
  BlockCategory,
  Division,
  LandBalance,
  PlantingMaterialType,
  Topography,
  LandType,
} from "../lib/types";
import { Toggle } from "./Toggle";

interface BlockFormValues {
  divisionId: string;
  code: string;
  category: BlockCategory;
  unplantedSubtype?: string;
  yearOfPlanting?: number;
  plantingMaterialType?: PlantingMaterialType;
  plantingMaterialDetails?: string;
  topography?: Topography;
  landType?: LandType;
  hectaresTotal?: string;
  hectaresPlanted?: string;
  sph?: number;
  palmCount?: number;
  palmCountOverride: boolean;
  palmNumberingScheme?: string;
  innerRoadToMill: boolean;
  millRoadKm?: string;
}

/** Auto-compute: SPH × hectaresPlanted → palm count (rounded). */
function computePalmCount(
  sph?: number,
  hectaresPlanted?: string,
): number | undefined {
  const ha = hectaresPlanted ? Number(hectaresPlanted) : NaN;
  if (!sph || Number.isNaN(ha) || ha <= 0) return undefined;
  return Math.round(sph * ha);
}

interface BlockFormProps {
  initial?: Block;
  /** Pre-select this division. When not set + only 1 division exists, auto-picks it. */
  defaultDivisionId?: string;
  onSaved: (block: Block) => void;
  onCancel: () => void;
}

const CATEGORY: BlockCategory[] = ["PLANTED", "LAND_CLEARING", "UNPLANTED"];
const MATERIAL: PlantingMaterialType[] = ["DXP", "TENERA", "CLONE", "RAMET", "OTHER"];
const TOPO: Topography[] = ["FLAT", "UNDULATING", "HILLY", "STEEP", "SWAMP"];
const LAND: LandType[] = ["MINERAL", "PEAT", "ALLUVIAL", "COASTAL", "BRIS", "OTHER"];
const UNPLANTED_SUBTYPES = [
  "A01_UNPLANTED_AREA",
  "A02_NURSERY",
  "A03_CONFLICTED_AREA",
  "A04_NOT_PLANTABLE_AREA",
  "A05_PALM_OIL_MILL",
  "A06_ROAD_HOUSING_DRAINAGE",
  "A07_HIGH_CONSERVATION_VALUE",
];

export function BlockForm({ initial, defaultDivisionId, onSaved, onCancel }: BlockFormProps) {
  const isEdit = Boolean(initial);
  const qc = useQueryClient();

  const [v, setV] = useState<BlockFormValues>({
    divisionId: initial?.divisionId ?? defaultDivisionId ?? "",
    code: initial?.code ?? "",
    category: initial?.category ?? "PLANTED",
    unplantedSubtype: initial?.unplantedSubtype ?? undefined,
    yearOfPlanting: initial?.yearOfPlanting ?? undefined,
    plantingMaterialType: initial?.plantingMaterialType ?? undefined,
    plantingMaterialDetails: initial?.plantingMaterialDetails ?? undefined,
    topography: initial?.topography ?? undefined,
    landType: initial?.landType ?? undefined,
    hectaresTotal: initial?.hectaresTotal ?? undefined,
    hectaresPlanted: initial?.hectaresPlanted ?? undefined,
    sph: initial?.sph ?? undefined,
    palmCount: initial?.palmCount ?? undefined,
    // Existing edit: if the stored palm_count doesn't match SPH × ha, the
    // block was already manually overridden — pre-check the override box.
    palmCountOverride:
      initial?.palmCount != null &&
      computePalmCount(initial?.sph ?? undefined, initial?.hectaresPlanted ?? undefined) !==
        initial.palmCount,
    palmNumberingScheme: initial?.palmNumberingScheme ?? undefined,
    innerRoadToMill: initial?.innerRoadToMill ?? false,
    millRoadKm: initial?.millRoadKm ?? undefined,
  });

  /**
   * Auto-computed palm count from SPH × hectaresPlanted. This is the
   * "system value" — used whenever palmCountOverride is false. The user
   * can flip Override on to enter a manual value (e.g. when the system
   * calc doesn't match a physical count due to gaps, dead palms, etc).
   */
  const autoPalmCount = computePalmCount(v.sph, v.hectaresPlanted);
  const effectivePalmCount = v.palmCountOverride ? v.palmCount : autoPalmCount;
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const divisionsQ = useQuery({
    queryKey: ["divisions"],
    queryFn: () => api.list<Division>("/v1/divisions"),
  });
  /**
   * Auto-select a sensible default division:
   *   - If only 1 division exists → auto-pick it
   *   - Else keep — select — and let the user choose
   * Only fires on create (not edit) and when divisionId is still empty.
   */
  useEffect(() => {
    if (isEdit) return;
    if (v.divisionId) return;
    const divs = divisionsQ.data?.data ?? [];
    if (divs.length === 1) {
      setV((prev) => ({ ...prev, divisionId: divs[0]!.id }));
    }
  }, [isEdit, divisionsQ.data, v.divisionId]);

  /**
   * Live title balance for the estate this block belongs to. Fetches the
   * current title cap + block-total, and subtracts what the user is
   * typing so the "remaining" number shifts as they type.
   */
  const currentEstateId = useMemo(() => {
    const d = (divisionsQ.data?.data ?? []).find((x) => x.id === v.divisionId);
    return d?.estateId ?? null;
  }, [divisionsQ.data, v.divisionId]);

  const balanceQ = useQuery({
    queryKey: ["land-balance", currentEstateId],
    queryFn: () =>
      api.get<LandBalance>(`/v1/estates/${currentEstateId}/land-balance`),
    enabled: !!currentEstateId,
  });

  const balance = balanceQ.data?.data;
  const currentHaOnBlock = isEdit ? Number(initial?.hectaresTotal ?? 0) : 0;
  const incomingHa = Number(v.hectaresTotal ?? 0);
  const remainingAfter =
    balance != null
      ? balance.titleAreaHa - (balance.blockAreaHa - currentHaOnBlock + (isNaN(incomingHa) ? 0 : incomingHa))
      : null;

  const submitMut = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { ...v };
      // Palm count: use override if set, else auto-computed value.
      // Never send the UI-only override flag to the API.
      payload.palmCount = effectivePalmCount;
      delete payload.palmCountOverride;
      // Strip empty strings; convert numeric strings to numbers where API expects.
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === undefined) delete payload[k];
      });
      if (isEdit) {
        delete payload.divisionId;
        const res = await api.patch<Block>(`/v1/blocks/${initial!.id}`, payload);
        return res.data;
      }
      const res = await api.post<Block>("/v1/blocks", payload);
      return res.data;
    },
    onSuccess: (block) => {
      qc.invalidateQueries({ queryKey: ["blocks"] });
      qc.invalidateQueries({ queryKey: ["block", block.id] });
      onSaved(block);
    },
    onError: (err) => {
      setFieldErrors({});
      setFormError(null);
      if (err instanceof ApiError) {
        const d = err.details as { fieldErrors?: Record<string, string[]> } | undefined;
        if (d?.fieldErrors) setFieldErrors(d.fieldErrors);
        else setFormError(err.message);
      } else {
        setFormError((err as Error).message ?? "Unknown error");
      }
    },
  });

  const field = (name: string) => fieldErrors[name]?.[0];

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submitMut.mutate();
      }}
      className="space-y-4"
    >
      {formError && (
        <div className="bg-bg-danger text-text-danger border border-border-danger rounded p-2 text-xs">
          {formError}
        </div>
      )}

      {/* Division */}
      <FormRow label="Division" name="divisionId" error={field("divisionId")}>
        <select
          className="input"
          value={v.divisionId}
          onChange={(e) => setV({ ...v, divisionId: e.target.value })}
          disabled={isEdit || divisionsQ.isLoading}
        >
          <option value="">— select —</option>
          {(divisionsQ.data?.data ?? []).map((d) => (
            <option key={d.id} value={d.id}>
              {d.code} · {d.name}
            </option>
          ))}
        </select>
      </FormRow>

      {/* Code */}
      <FormRow label="Block code" name="code" hint="3 chars — e.g. 001, 041, A01" error={field("code")}>
        <input
          className="input font-mono"
          value={v.code}
          onChange={(e) => setV({ ...v, code: e.target.value })}
          maxLength={8}
        />
      </FormRow>

      {/* Category radios */}
      <FormRow label="Category" name="category" error={field("category")}>
        <div className="flex gap-2 flex-wrap">
          {CATEGORY.map((c) => (
            <label
              key={c}
              className={`px-2.5 py-1 rounded-full border text-xs cursor-pointer transition-colors ${
                v.category === c
                  ? "bg-bg-accent text-text-accent border-border-accent font-medium"
                  : "bg-surface-1 border-border-strong text-text-secondary hover:border-border-strong"
              }`}
            >
              <input
                type="radio"
                name="category"
                value={c}
                checked={v.category === c}
                onChange={() => setV({ ...v, category: c })}
                className="sr-only"
              />
              {c === "LAND_CLEARING" ? "Land clearing" : c === "PLANTED" ? "Planted" : "Unplanted"}
            </label>
          ))}
        </div>
      </FormRow>

      {/* Conditional: UNPLANTED subtype */}
      {v.category === "UNPLANTED" && (
        <FormRow
          label="Unplanted sub-type"
          name="unplantedSubtype"
          error={field("unplantedSubtype")}
          hint="Required when category = UNPLANTED"
        >
          <select
            className="input"
            value={v.unplantedSubtype ?? ""}
            onChange={(e) => setV({ ...v, unplantedSubtype: e.target.value || undefined })}
          >
            <option value="">— select —</option>
            {UNPLANTED_SUBTYPES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </FormRow>
      )}

      {/* Year of clearing — LAND_CLEARING only. Rolls to Year of planting
          when the block is re-categorized to PLANTED later. */}
      {v.category === "LAND_CLEARING" && (
        <FormRow
          label="Year of clearing"
          name="yearOfPlanting"
          hint="Rolls forward to Year of planting when category changes to Planted"
          error={field("yearOfPlanting")}
        >
          <input
            type="number"
            className="input font-mono"
            min="1900"
            max="2100"
            value={v.yearOfPlanting ?? ""}
            onChange={(e) =>
              setV({ ...v, yearOfPlanting: e.target.value ? Number(e.target.value) : undefined })
            }
          />
        </FormRow>
      )}

      {/* Conditional: PLANTED */}
      {v.category === "PLANTED" && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <FormRow
              label="Year of planting"
              name="yearOfPlanting"
              error={field("yearOfPlanting")}
            >
              <input
                type="number"
                className="input font-mono"
                min="1900"
                max="2100"
                value={v.yearOfPlanting ?? ""}
                onChange={(e) =>
                  setV({ ...v, yearOfPlanting: e.target.value ? Number(e.target.value) : undefined })
                }
              />
            </FormRow>
            <FormRow
              label="Planting material"
              name="plantingMaterialType"
              error={field("plantingMaterialType")}
            >
              <select
                className="input"
                value={v.plantingMaterialType ?? ""}
                onChange={(e) =>
                  setV({
                    ...v,
                    plantingMaterialType: (e.target.value || undefined) as PlantingMaterialType,
                  })
                }
              >
                <option value="">— select —</option>
                {MATERIAL.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </FormRow>
          </div>
          <FormRow
            label="Material details"
            name="plantingMaterialDetails"
            hint="e.g. GH500 × Yangambi, AAR Progeny 3"
          >
            <input
              className="input"
              value={v.plantingMaterialDetails ?? ""}
              onChange={(e) =>
                setV({ ...v, plantingMaterialDetails: e.target.value || undefined })
              }
            />
          </FormRow>
        </>
      )}

      {/* Common: hectares · with live title-balance hint */}
      <div className="grid grid-cols-2 gap-3">
        <FormRow
          label="Hectares total"
          name="hectaresTotal"
          error={field("hectaresTotal")}
          hint={
            balance
              ? balance.titleAreaHa > 0
                ? `Title cap ${balance.titleAreaHa.toFixed(2)} · used ${(balance.blockAreaHa - currentHaOnBlock).toFixed(2)} · remaining after save ${remainingAfter?.toFixed(2) ?? "?"} ha`
                : "No title linked to this estate yet — no cap enforced"
              : undefined
          }
        >
          <input
            className={`input font-mono ${remainingAfter != null && remainingAfter < 0 ? "border-red-500" : ""}`}
            inputMode="decimal"
            placeholder="25.40"
            value={v.hectaresTotal ?? ""}
            onChange={(e) => setV({ ...v, hectaresTotal: e.target.value || undefined })}
          />
        </FormRow>
        <FormRow
          label="Hectares planted"
          name="hectaresPlanted"
          error={field("hectaresPlanted")}
          hint={
            v.category === "UNPLANTED"
              ? "N/A · unplanted category has no planted area"
              : "≤ total"
          }
        >
          <input
            className={`input font-mono ${v.category === "UNPLANTED" ? "opacity-50" : ""}`}
            inputMode="decimal"
            placeholder={v.category === "UNPLANTED" ? "N/A" : "24.85"}
            value={v.category === "UNPLANTED" ? "" : (v.hectaresPlanted ?? "")}
            onChange={(e) => setV({ ...v, hectaresPlanted: e.target.value || undefined })}
            disabled={v.category === "UNPLANTED"}
          />
        </FormRow>
      </div>

      {/* Live overflow warning banner — only when there IS a title cap.
          Without a linked title, the balance calc's titleAreaHa is 0 and
          would falsely flag every block as "over cap". */}
      {balance != null &&
        balance.titleAreaHa > 0 &&
        remainingAfter != null &&
        remainingAfter < 0 && (
          <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
            <strong>Over title cap by {Math.abs(remainingAfter).toFixed(2)} ha.</strong>{" "}
            Reduce this block's hectares or add title capacity on the Estate
            (Titles button) before saving.
          </div>
        )}

      {/* Palm density — SPH + total palm count (auto-computed) + numbering */}
      {v.category === "PLANTED" && (
        <div className="grid grid-cols-3 gap-3">
          <FormRow
            label="SPH"
            name="sph"
            error={field("sph")}
            hint="stand/ha · 130–150 typical"
          >
            <input
              className="input font-mono"
              inputMode="numeric"
              placeholder="136"
              value={v.sph ?? ""}
              onChange={(e) =>
                setV({
                  ...v,
                  sph: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            />
          </FormRow>
          <FormRow
            label={
              <span className="flex items-center gap-2">
                Palm count
                <label className="flex items-center gap-1 text-[10px] font-normal text-text-secondary">
                  <input
                    type="checkbox"
                    checked={v.palmCountOverride}
                    onChange={(e) =>
                      setV({
                        ...v,
                        palmCountOverride: e.target.checked,
                        // When switching back to auto, drop the manual value.
                        palmCount: e.target.checked ? v.palmCount : undefined,
                      })
                    }
                  />
                  Override
                </label>
              </span>
            }
            name="palmCount"
            error={field("palmCount")}
            hint={
              v.palmCountOverride
                ? "Manual override — you're editing directly"
                : autoPalmCount
                  ? `Auto = SPH × ha planted = ${autoPalmCount}`
                  : "Set SPH + hectares planted to auto-compute"
            }
          >
            <input
              className={`input font-mono ${!v.palmCountOverride ? "opacity-70" : ""}`}
              inputMode="numeric"
              placeholder={autoPalmCount ? String(autoPalmCount) : "3380"}
              value={
                v.palmCountOverride
                  ? (v.palmCount ?? "")
                  : (autoPalmCount ?? "")
              }
              readOnly={!v.palmCountOverride}
              onChange={(e) =>
                setV({
                  ...v,
                  palmCount: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            />
          </FormRow>
          <FormRow
            label="Palm numbering scheme"
            name="palmNumberingScheme"
            error={field("palmNumberingScheme")}
            hint="e.g. 001-R{row}-P{palm}"
          >
            <input
              className="input font-mono"
              placeholder="001-R{row}-P{palm}"
              value={v.palmNumberingScheme ?? ""}
              onChange={(e) =>
                setV({
                  ...v,
                  palmNumberingScheme: e.target.value || undefined,
                })
              }
            />
          </FormRow>
        </div>
      )}

      {/* Topography + Land type */}
      <div className="grid grid-cols-2 gap-3">
        <FormRow label="Topography" name="topography">
          <select
            className="input"
            value={v.topography ?? ""}
            onChange={(e) =>
              setV({ ...v, topography: (e.target.value || undefined) as Topography })
            }
          >
            <option value="">—</option>
            {TOPO.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </FormRow>
        <FormRow label="Land type" name="landType">
          <select
            className="input"
            value={v.landType ?? ""}
            onChange={(e) =>
              setV({ ...v, landType: (e.target.value || undefined) as LandType })
            }
          >
            <option value="">—</option>
            {LAND.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </FormRow>
      </div>

      {/* Delivery flags */}
      <div className="border-t border-border pt-3">
        <div className="text-[11px] text-text-secondary uppercase tracking-wide mb-2">
          Delivery flags
        </div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-medium">Inner road to mill</div>
            <div className="text-[11px] text-text-secondary">
              Block reachable via estate's private inner road (enables Direct·Grabber routing)
            </div>
          </div>
          <Toggle
            checked={v.innerRoadToMill}
            onChange={(checked) => setV({ ...v, innerRoadToMill: checked })}
          />
        </div>
        {v.innerRoadToMill && (
          <FormRow
            label="Distance to mill via inner road"
            name="millRoadKm"
            error={field("millRoadKm")}
            hint="Grabber-direct requires ≤ 5 km"
          >
            <div className="flex items-center gap-2">
              <input
                className="input font-mono max-w-[120px]"
                inputMode="decimal"
                placeholder="1.20"
                value={v.millRoadKm ?? ""}
                onChange={(e) => setV({ ...v, millRoadKm: e.target.value || undefined })}
              />
              <span className="text-xs text-text-secondary">km</span>
            </div>
          </FormRow>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={submitMut.isPending}>
          {submitMut.isPending ? "Saving…" : isEdit ? "Save changes" : "Create block"}
        </button>
      </div>
    </form>
  );
}

function FormRow({
  label,
  name: _name,
  hint,
  error,
  children,
}: {
  label: React.ReactNode;
  name: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1">{label}</label>
      {children}
      {hint && !error && <div className="text-[10.5px] text-text-muted mt-1">{hint}</div>}
      {error && <div className="text-[10.5px] text-text-danger mt-1">{error}</div>}
    </div>
  );
}
